import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useAuth } from '../auth';

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [aboutMe, setAboutMe] = useState(user?.about_me ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!user) return <p className="muted">Loading…</p>;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSaved(false);
    try {
      const payload: Parameters<typeof updateProfile>[0] = {
        name,
        email,
        about_me: aboutMe,
      };
      if (password) {
        payload.current_password = currentPassword;
        payload.password = password;
        payload.password_confirmation = passwordConfirmation;
      }
      await updateProfile(payload);
      setPassword('');
      setPasswordConfirmation('');
      setCurrentPassword('');
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as {
          errors?: Record<string, string[]>; message?: string;
        } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? 'Update failed');
      } else {
        setError('Update failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const pending = user.approved_at === null;

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <h2>Your profile</h2>
      {pending && (
        <p style={{ background: '#ffe', border: '1px solid #cc8', padding: '8px 12px', borderRadius: 4, fontSize: 13 }}>
          Your account is <strong>pending administrator approval</strong>.
          You can update your details below while you wait — anything you
          set here helps the admin make a decision.
        </p>
      )}
      <form onSubmit={onSubmit}>
        <div className="row">
          <label htmlFor="name">Name</label>
          <input id="name" type="text" maxLength={64}
            value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="row">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="row">
          <label htmlFor="about_me">About you</label>
          <textarea id="about_me" maxLength={2000} rows={5}
            value={aboutMe} onChange={(e) => setAboutMe(e.target.value)} />
        </div>

        <fieldset style={{ border: '1px solid #ddd', padding: 12, marginTop: 12 }}>
          <legend style={{ fontSize: 13 }}>Change password (optional)</legend>
          <div className="row">
            <label htmlFor="current_password">Current password</label>
            <input id="current_password" type="password" autoComplete="current-password"
              value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div className="row">
            <label htmlFor="password">New password (min 12 chars)</label>
            <input id="password" type="password" minLength={12} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="row">
            <label htmlFor="password_confirmation">Confirm new password</label>
            <input id="password_confirmation" type="password" minLength={12} autoComplete="new-password"
              value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} />
          </div>
        </fieldset>

        <button type="submit" disabled={submitting} style={{ marginTop: 12 }}>
          {submitting ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <p style={{ color: '#070', fontSize: 13, marginTop: 8 }}>Saved.</p>}
        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      </form>
    </div>
  );
}
